import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Alert, Button, Checkbox } from '@atoms'
import TextInput from '@components/atoms/form/TextInput'
import { PageLayout } from '@components/layouts/PageLayout'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { SelectField } from '@components/molecules/SelectField'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'

import { DesignExplorer, SegControl } from '../components/DesignExplorer'
import { ImportSummary } from '../components/imports/ImportSummary'
import { EVENTS, GROUP, type ImportRun, MAX_ROWS, SETTLED_RUN, WORKING_RUN } from '../components/imports/sampleData'
import { AlertText, Card, CardHeading, inlineLinkClasses } from '../components/imports/ui'

/**
 * One-page import inside a group (design-phase prototype). The group comes
 * from the URL, so there is no group field. Pick what you are importing, fill
 * in what that type needs, then upload. Building a custom template moves to
 * its own page.
 *
 * Sample data only. Nothing here talks to a controller, GraphQL or CSVBox.
 */

type V2ImportType = 'group_members' | 'event_rsvps' | 'events_shifts'

type SummaryState = 'working' | 'settled'

const MAX_SHIFTS = { default: 10, min: 0, max: 200 } as const

const GROUP_EVENTS = EVENTS.filter((event) => event.group === GROUP.name)

const HEADINGS: Record<V2ImportType, string> = {
  group_members: 'Import group members',
  event_rsvps: 'Import event RSVPs',
  events_shifts: 'Import events and shifts',
}

const TEMPLATE_FILES: Record<V2ImportType, string> = {
  group_members: 'warringah-volunteers-members.csv',
  event_rsvps: 'warringah-volunteers-rsvps.csv',
  events_shifts: 'warringah-volunteers-events.csv',
}

const TEMPLATE_BUILDERS: Record<V2ImportType, string> = {
  group_members: '/previews/supporter_import_preview?step=template',
  event_rsvps: '/previews/supporter_import_preview?step=template',
  events_shifts: '/previews/event_imports_preview?step=template',
}

const BEFORE_SUBMIT = [
  'Your column names are matched to the template, even if you renamed or moved them.',
  'Problem cells are highlighted so you can fix them on screen.',
  `Files with more than ${MAX_ROWS.toLocaleString()} rows are stopped here.`,
]

const AFTER_SUBMIT = [
  'SupporterBase works through the rows in the background.',
  'The import summary fills in as it goes, and we email you when it finishes.',
  'Rows that could not be imported are listed in the summary with the reason.',
]

function maxShiftsError(value: string): string | null {
  if (value.trim() === '') return 'Enter the most shifts any one event has.'
  const count = Number(value)
  if (!Number.isInteger(count)) return 'Enter a whole number.'
  if (count < MAX_SHIFTS.min || count > MAX_SHIFTS.max)
    return `Enter a number from ${MAX_SHIFTS.min} to ${MAX_SHIFTS.max}.`
  return null
}

type OptionsForm = { markAsAttended: boolean }

function ImportTypeCard({
  type,
  onTypeChange,
  eventId,
  onEventChange,
  maxShifts,
  onMaxShiftsChange,
  maxShiftsTouched,
  onMaxShiftsBlur,
  optionsControl,
}: {
  type: V2ImportType
  onTypeChange: (type: V2ImportType) => void
  eventId: string | null
  onEventChange: (eventId: string | null) => void
  maxShifts: string
  onMaxShiftsChange: (value: string) => void
  maxShiftsTouched: boolean
  onMaxShiftsBlur: () => void
  optionsControl: ReturnType<typeof useForm<OptionsForm>>['control']
}) {
  const event = GROUP_EVENTS.find((candidate) => candidate.id === eventId) ?? null
  const shiftsError = maxShiftsTouched ? maxShiftsError(maxShifts) : null

  return (
    <Card>
      <CardHeading icon="file-05" title="What are you importing?" />
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-5">
        <RadioGroupField<V2ImportType>
          label="Import type"
          appearance="cards"
          value={type}
          onValueChange={onTypeChange}
          options={[
            {
              value: 'group_members',
              label: 'Group members',
              description: `Add people to ${GROUP.name}, or update people already in it.`,
            },
            {
              value: 'event_rsvps',
              label: 'Event RSVPs',
              description: "Record who signed up for one of this group's events.",
            },
            {
              value: 'events_shifts',
              label: 'Events and shifts',
              description: 'Create events for this group, each with its shifts.',
            },
          ]}
        />

        {type === 'event_rsvps' && (
          <div className="tw:flex tw:max-w-md tw:flex-col tw:gap-3">
            <SelectField
              search
              required
              name="event"
              label="Event"
              description="Every row RSVPs to this one event."
              placeholder="Choose an event"
              options={GROUP_EVENTS.map((candidate) => ({
                value: candidate.id,
                label: `${candidate.name} · ${candidate.date}${candidate.past ? ' (past)' : ''}`,
              }))}
              value={eventId}
              onValueChange={onEventChange}
            />
            {event?.past && (
              <div className="tw:flex tw:flex-wrap tw:items-start">
                <Checkbox
                  control={optionsControl}
                  name="markAsAttended"
                  description="Only offered because this event has already happened."
                >
                  Mark everyone in the file as attended
                </Checkbox>
              </div>
            )}
          </div>
        )}

        {type === 'events_shifts' && (
          <div className="tw:max-w-xs">
            <TextInput
              required
              type="number"
              name="max_shifts"
              label="Max shifts per event"
              description={`The most shifts any one event in your file has, up to ${MAX_SHIFTS.max}. Use 0 for events with no shifts.`}
              min={MAX_SHIFTS.min}
              max={MAX_SHIFTS.max}
              step={1}
              value={maxShifts}
              onChange={(change) => onMaxShiftsChange(change.target.value)}
              onBlur={onMaxShiftsBlur}
              errors={shiftsError ? { max_shifts: { type: 'validate', message: shiftsError } } : undefined}
            />
          </div>
        )}
      </div>
    </Card>
  )
}

function TemplateLinks({
  type,
  onDownload,
  hasDownloaded,
}: {
  type: V2ImportType
  onDownload: () => void
  hasDownloaded: boolean
}) {
  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-2 tw:border-t tw:border-gray-100 tw:px-5 tw:py-4 tw:text-sm">
      <span className="tw:text-gray-cool-600">Need a file to fill in?</span>
      <button type="button" onClick={onDownload} className={cn(inlineLinkClasses, 'tw:flex tw:items-center tw:gap-1')}>
        <Icon type="file-download-03" className="tw:h-4 tw:w-4" aria-hidden />
        Download a template
      </button>
      <a href={TEMPLATE_BUILDERS[type]} className={cn(inlineLinkClasses, 'tw:flex tw:items-center tw:gap-1')}>
        <Icon type="columns-03" className="tw:h-4 tw:w-4" aria-hidden />
        Build a template with the columns you need
      </a>
      <span role="status" className="tw:w-full tw:text-xs tw:text-gray-cool-600">
        {hasDownloaded && (
          <span className="tw:flex tw:items-center tw:gap-1.5">
            <Icon type="check-circle" className="tw:h-4 tw:w-4 tw:text-success-600" aria-hidden />
            Downloaded <span className="tw:font-mono">{TEMPLATE_FILES[type]}</span>
          </span>
        )}
      </span>
    </div>
  )
}

function UploadCard({
  type,
  missing,
  eventName,
  onFileChosen,
  onDownload,
  hasDownloaded,
}: {
  type: V2ImportType
  missing: string | null
  eventName: string | null
  onFileChosen: () => void
  onDownload: () => void
  hasDownloaded: boolean
}) {
  const reasonId = useId()
  const destination =
    type === 'group_members'
      ? `Every row goes into ${GROUP.name}.`
      : type === 'event_rsvps'
        ? `Every row is signed up for ${eventName}.`
        : `Every event is created in ${GROUP.name}.`

  return (
    <Card>
      <CardHeading
        icon="upload-cloud-02"
        title="Upload your file"
        subtitle="CSV or Excel. Problems are highlighted on screen so you can fix them before you submit."
      />
      <div className="tw:p-5">
        <div
          className={cn(
            'tw:flex tw:flex-col tw:items-center tw:gap-3 tw:rounded-xl tw:border-2 tw:border-dashed tw:px-6 tw:py-10 tw:text-center',
            missing ? 'tw:border-gray-cool-200 tw:bg-gray-cool-50' : 'tw:border-primary-200 tw:bg-primary-50/40'
          )}
        >
          <span
            aria-hidden
            className={cn(
              'tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-white tw:shadow-xs',
              missing ? 'tw:text-gray-cool-400' : 'tw:text-primary-600'
            )}
          >
            <Icon type="upload-cloud-02" className="tw:h-6 tw:w-6" />
          </span>
          {missing ? (
            <p id={reasonId} className="tw:mb-0 tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-gray-cool-700">
              <Icon type="info-circle" className="tw:h-4 tw:w-4 tw:shrink-0" aria-hidden />
              {missing}
            </p>
          ) : (
            <>
              <p className="tw:mb-0 tw:text-sm tw:font-medium tw:text-gray-cool-900">Choose the file you filled in</p>
              <p className="tw:mb-0 tw:max-w-md tw:text-xs tw:text-gray-cool-600">
                Up to {MAX_ROWS.toLocaleString()} rows. {destination}
              </p>
            </>
          )}
          <Button
            size="xl"
            leadingIcon="plus-circle"
            onClick={onFileChosen}
            disabled={Boolean(missing)}
            {...(missing && { 'aria-describedby': reasonId })}
          >
            Choose file
          </Button>
        </div>
      </div>
      <TemplateLinks type={type} onDownload={onDownload} hasDownloaded={hasDownloaded} />
    </Card>
  )
}

function ChecksCard({ title, items }: { title: string; items: string[] }) {
  return (
    <Card>
      <CardHeading icon="check-done-01" title={title} />
      <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100 tw:px-5 tw:text-sm">
        {items.map((line) => (
          <li key={line} className="tw:flex tw:items-start tw:gap-2.5 tw:py-3 tw:text-gray-cool-700">
            <Icon type="check-circle" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-success-600" aria-hidden />
            {line}
          </li>
        ))}
      </ul>
    </Card>
  )
}

function EventsImportStarted({ onReset }: { onReset: () => void }) {
  return (
    <Alert type="success" icon="check-circle">
      <AlertText>
        Your events import has started. In the app this opens its import summary, which is designed in the{' '}
        <a href="/previews/event_imports_preview?step=confirm" className={inlineLinkClasses}>
          event imports preview
        </a>
        .{' '}
        <button type="button" onClick={onReset} className={inlineLinkClasses}>
          Start another import
        </button>
      </AlertText>
    </Alert>
  )
}

function runFor(
  state: SummaryState,
  type: 'group_members' | 'event_rsvps',
  eventName: string | null,
  markAsAttended: boolean
): ImportRun {
  const base = state === 'working' ? WORKING_RUN : SETTLED_RUN
  return {
    ...base,
    type: type === 'group_members' ? 'supporters' : 'event_rsvps',
    group: GROUP.name,
    fileName: TEMPLATE_FILES[type],
    event: type === 'event_rsvps' ? eventName : null,
    overwrite: false,
    markAsAttended: type === 'event_rsvps' && markAsAttended,
  }
}

const V2ImportPreview = () => {
  const [type, setType] = useState<V2ImportType>('group_members')
  const [eventId, setEventId] = useState<string | null>(null)
  const [maxShifts, setMaxShifts] = useState(String(MAX_SHIFTS.default))
  const [maxShiftsTouched, setMaxShiftsTouched] = useState(false)
  const [hasDownloaded, setHasDownloaded] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [summaryState, setSummaryState] = useState<SummaryState>('working')

  const {
    control: optionsControl,
    watch,
    reset: resetOptions,
  } = useForm<OptionsForm>({ defaultValues: { markAsAttended: false } })
  const markAsAttended = watch('markAsAttended')

  const event = GROUP_EVENTS.find((candidate) => candidate.id === eventId) ?? null

  const missing =
    type === 'event_rsvps' && !event
      ? 'Choose the event above before you upload.'
      : type === 'events_shifts' && maxShiftsError(maxShifts)
        ? 'Enter the max shifts per event above before you upload.'
        : null

  const changeType = (next: V2ImportType) => {
    setType(next)
    setHasDownloaded(false)
  }

  const changeMaxShifts = (value: string) => {
    setMaxShifts(value)
    setMaxShiftsTouched(true)
  }

  const startAgain = () => {
    setSubmitted(false)
    setEventId(null)
    setMaxShifts(String(MAX_SHIFTS.default))
    setMaxShiftsTouched(false)
    setHasDownloaded(false)
    resetOptions()
  }

  const submitFile = () => {
    setSummaryState('working')
    setSubmitted(true)
  }

  return (
    <PageLayout.Root>
      <PageLayout.Header
        icon="upload-cloud-02"
        heading={HEADINGS[type]}
        subtitle={
          <>
            {type === 'event_rsvps' ? 'For' : 'Into'}{' '}
            <span className="tw:font-medium tw:text-gray-cool-900">{GROUP.name}</span> ·{' '}
            {GROUP.supporterCount.toLocaleString()} supporters
          </>
        }
      />
      <PageLayout.Body>
        <div className="tw:flex tw:max-w-4xl tw:flex-col tw:gap-5">
          {submitted && type === 'events_shifts' && <EventsImportStarted onReset={startAgain} />}

          {submitted && type !== 'events_shifts' && (
            <>
              <ImportSummary
                run={runFor(summaryState, type, event?.name ?? null, markAsAttended)}
                viewer="captain"
                embedded
              />
              <div>
                <Button rank="secondary" onClick={startAgain}>
                  Start another import
                </Button>
              </div>
            </>
          )}

          {!submitted && (
            <>
              <ImportTypeCard
                type={type}
                onTypeChange={changeType}
                eventId={eventId}
                onEventChange={setEventId}
                maxShifts={maxShifts}
                onMaxShiftsChange={changeMaxShifts}
                maxShiftsTouched={maxShiftsTouched}
                onMaxShiftsBlur={() => setMaxShiftsTouched(true)}
                optionsControl={optionsControl}
              />
              <UploadCard
                type={type}
                missing={missing}
                eventName={event?.name ?? null}
                onFileChosen={submitFile}
                onDownload={() => setHasDownloaded(true)}
                hasDownloaded={hasDownloaded}
              />
              <div className="tw:grid tw:gap-5 tw:md:grid-cols-2">
                <ChecksCard title="Before you submit" items={BEFORE_SUBMIT} />
                <ChecksCard title="After you submit" items={AFTER_SUBMIT} />
              </div>
            </>
          )}
        </div>
      </PageLayout.Body>

      <DesignExplorer>
        <SegControl
          label="Page"
          value={submitted ? 'submitted' : 'form'}
          onChange={(value) => (value === 'submitted' ? submitFile() : setSubmitted(false))}
          options={[
            { value: 'form', label: 'Form' },
            { value: 'submitted', label: 'Submitted' },
          ]}
        />
        <SegControl
          label="Summary state"
          value={summaryState}
          onChange={(value) => setSummaryState(value)}
          options={[
            { value: 'working', label: 'Working' },
            { value: 'settled', label: 'Settled' },
          ]}
        />
      </DesignExplorer>
    </PageLayout.Root>
  )
}

export default V2ImportPreview
